import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, Avatar, CircularProgress, Tooltip, useTheme, Divider } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";
import queryGraphql from "../../helpers/ApiInterface";
import MiniBot from '../MiniBot';
import { useAuth } from '../../helpers/AuthProvider';

const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

// Define an interface for the nested data structures based on the new query
interface ApplicationData {
    id: string;
    applicationName: string;
}

interface SolutionOwnerData {
    id: string;
    name: string;
}

interface AIAgentDetails {
    projectId: string;
    region: string;
    imageUrl: string;
    gpu: string;
    modelName: string;
    modelTemperature: number;
    modelMaxTokens: number;
    modelStreaming: boolean;
    repoUrl?: string;
    network?: string;
    subnet?: string;
}

// Updated AIAgentData interface based STRICTLY on the new query
interface AIAgentData {
    id: string;
    name: string;
    solutionType: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    environment: string;
    details: {
        aiAgent: AIAgentDetails;
    };
    status: string;
    solutionOwner: SolutionOwnerData;
    application?: ApplicationData; // Application can be optional
    reasonForRejection?: string
}

const AIAgentDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const {id}  = useParams();
    const [aiAgent, setAiAgent] = useState<AIAgentData | null>(null);
    const [agentEndpoint, setAgentEndpoint] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();

    useEffect(() => {
        const fetchAIAgentData = async () => {
            if (!id) {
                setIsLoading(false);
                setError("Agent ID is missing from URL.");
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const query = `
                    query GetSolutionAIAgent($getSolutionAiAgentId: String!) {
                      getSolutionAIAgent(id: $getSolutionAiAgentId) {
                        id
                        name
                        solutionType
                        cloudProvider
                        reasonForRejection
                        environment
                        details {
                          aiAgent {
                            projectId
                            region
                            imageUrl
                            gpu
                            modelName
                            modelTemperature
                            modelMaxTokens
                            modelStreaming
                            repoUrl

                            network
                            subnet
                          }
                        }
                        solutionOwner {
                          id
                          name
                        }
                        application {
                          id
                          applicationName
                        }
                        status
                      }
                    }
                `;
                const response = await queryGraphql(query, { getSolutionAiAgentId: id });
                
                if (response.getSolutionAIAgent) {
                    setAiAgent(response.getSolutionAIAgent);
                }
                else if (response.errors && response.errors.length > 0) {
                    setError(response.errors[0].message || "Error fetching data.");
                    setAiAgent(null);
                }
                else {
                    setError("AI Agent not found or unexpected response structure.");
                    setAiAgent(null);
                }
            } catch (err: any) {
                console.error("Error fetching AI Agent data:", err);
                setError(err.message || "Failed to fetch AI Agent details.");
                setAiAgent(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAIAgentData();
    }, [id]);

    useEffect(() => {
        const fetchEndpointUrl = async () => {
            if (!aiAgent || aiAgent.status !== 'active' || !aiAgent.id) return;
            try {
                const data = await queryGraphql(
                    `
                    query Query($resourceStatusId: String!) {
                      resourceStatus(id: $resourceStatusId) {
                        id
                        resourceName
                        solutionEndpointUrl
                      }
                    }
                    `, { resourceStatusId: aiAgent.id }
                );
                if (data.resourceStatus?.solutionEndpointUrl) {
                    setAgentEndpoint(data.resourceStatus.solutionEndpointUrl);
                }
            } catch (error) {
                console.log("Error fetching agent endpoint URL:", error);
            }
        };

        fetchEndpointUrl();
    }, [aiAgent]);


    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography sx={{ ml: 2 }}>Loading agent details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{p:2}}>{error}</Typography>;
    }

    if (!aiAgent) {
        return <Typography sx={{p:2}}>No agent data available. Ensure the agent ID is correct and the API is reachable.</Typography>;
    }

    const resourceOwnerName = aiAgent.solutionOwner.name;

    const getStatusBackgroundColor = (status: string | undefined): string => {
        if (!status) return "#F8F8F8"; // Default
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved") {
            return "#12B76A10";
        } else if (lowerStatus === "rejected") {
            return "#F0443810";
        } else if (lowerStatus === "pending") {
            return "#FDB02210";
        }
        return "#F8F8F8";
    };
    
    const projectIdLabel = aiAgent.cloudProvider === 'aws' ? 'Account ID' : aiAgent.cloudProvider === 'azure' ? 'Subscription ID' : 'Project ID';

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/ai-agents" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/aisolutions/aiagent'); }}> AI Agents </Link>
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Agent Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/aisolutions/aiagent')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Agent Details </Typography>
            </Box>

            <Box sx={{mx:0, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{
                    p: 2.5,
                    bgcolor: getStatusBackgroundColor(aiAgent.status),
                    mb: 0,
                    mx: 0,
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                                {aiAgent.name}
                            </Typography>
                            {user.roles?.some((e: string) => ["cluster_owner"].includes(e)) && aiAgent.status === "pending" && (
                                <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/aisolutions/aiagent/edit/${id}`)}>
                                    <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                            Resource Owner: {resourceOwnerName}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={3} sx={{p:3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>{projectIdLabel}</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.details.aiAgent.projectId}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Cloud Provider</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333', textTransform: 'uppercase'}}>{aiAgent.cloudProvider}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Region</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.details.aiAgent.region}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Image URL</Typography>
                        <Typography variant="body1"  sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333', wordBreak: 'break-all'}}>{aiAgent.details.aiAgent.imageUrl}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>GPU</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.details.aiAgent.gpu}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Environment</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.environment}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Application Name</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.application?.applicationName || 'N/A'}</Typography>
                    </Grid>

                    {agentEndpoint && (
                        <Grid item xs={12} sm={5} md={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Agent Endpoint</Typography>
                            <Tooltip
                                sx={{ whiteSpace: "pre-line" }}
                                title={
                                    <Link
                                        href={agentEndpoint}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'block',
                                            wordBreak: 'break-all',
                                            color: '#ffffff',
                                            textDecoration: 'underline',
                                            fontFamily: "Gilroy-Regular",
                                            fontSize: "14px",
                                        }}
                                    >
                                        {agentEndpoint}
                                    </Link>
                                }
                                slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
                            >
                                <Avatar
                                    src="/assets/info.svg"
                                    alt="info"
                                    sx={{ height: 16, width: 16, cursor: 'pointer' }}
                                />
                            </Tooltip>
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ mt: 0, p: 2.5, borderTop: "1px solid #e0e0e0" }}>
                    <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
                        Model Details
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Model Name</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.details.aiAgent.modelName}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Model Temperature</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.details.aiAgent.modelTemperature}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Model Tokens</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.details.aiAgent.modelMaxTokens}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Model Streaming</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.details.aiAgent.modelStreaming ? "True" : "False"}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                
            </Box>
            {aiAgent.status === 'rejected' && (
                                                        <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                                                        <Divider sx={{ marginY: 2 }} />
                                                        <DetailItem label="Reason for Rejection" value={aiAgent.reasonForRejection} />
                                                        </Box>
                                                )}

            <Box sx={{ mt: 0, p: 0, borderTop: "1px solid #e0e0e0" }}>
                
                                {aiAgent.status === 'active' && aiAgent.cloudProvider != 'gcp' && (
                                                             <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
                                                                <Divider sx={{ marginY: 2 }} />
                                                                <Grid container spacing={2.5}>
                                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Network" value={aiAgent.details.aiAgent.network} /></Grid>
                                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Subnets" value={aiAgent.details.aiAgent.subnet} /></Grid>

                                                                </Grid>
                                                             </Box>
                                                        )}
                                                        </Box>
        </Container>
    );
};


const AIAgentDetailsPage: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"aisolutions-aiagent"}
                onStateChange={handleDrawerStateChange}
            />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: {xs: 0, sm: `${currentMarginLeft}px`},
                }}
            >
                <AIAgentDetailsPageContent />
            </Box>
        </Box>
    );
};

export default AIAgentDetailsPage;
// import React, { useState, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, Avatar, CircularProgress, Tooltip, useTheme } from '@mui/material';
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import moment from 'moment';
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed
// import queryGraphql from "../../helpers/ApiInterface";
// import MiniBot from '../MiniBot';
// import { useAuth } from '../../helpers/AuthProvider';
// // import { useAuth } from "../../helpers/AuthProvider"; // Uncomment if needed

// // Define an interface for the AI Agent data based on your GraphQL query
// interface AIAgentOwner {
//     name: string;
//     email: string;
//     googleProfilePicture: string;
// }

// interface AIAgentData {
//     id: string;
//     agentName: string;
//     aIAgentOwner: AIAgentOwner;
//     projectId: string;
//     serviceName: string;
//     region: string;
//     imageUrl: string;
//     gpu: string;
//     applicationName: string;
//     modelName: string;
//     modelTemperature: number;
//     modelMaxTokens: number;
//     modelStreaming: boolean;
//     updatedAt: string;
//     createdAt: string;
//     repoUrl?: string;
//     environment: string;
//     status?: string; // Added status to interface
//     reasonForRejection?: string; // Added reasonForRejection to interface
// }

// const AIAgentDetailsPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const { user } = useAuth();
//     const { id } = useParams<{ id: string }>();
//     const [aiAgent, setAiAgent] = useState<AIAgentData | null>(null);
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const theme = useTheme(); // Called useTheme to access theme properties

//     useEffect(() => {
//         const fetchAIAgentData = async () => {
//             if (!id) {
//                 setIsLoading(false);
//                 setError("Agent ID is missing from URL.");
//                 return;
//             }

//             setIsLoading(true);
//             setError(null);
//             try {
//                 const query = `
//                     query GetAIAgent($getAiAgentId: String!) {
//                         getAIAgent(id: $getAiAgentId) {
//                             id
//                             agentName
//                             aIAgentOwner {
//                                 name
//                                 email
//                                 googleProfilePicture
//                             }
//                             projectId
//                             serviceName
//                             region
//                             imageUrl
//                             environment
//                             gpu
//                             applicationName
//                             modelName
//                             modelTemperature
//                             modelMaxTokens
//                             modelStreaming
//                             updatedAt
//                             createdAt
//                             repoUrl
//                             status # Ensure status is fetched
//                             reasonForRejection # Ensure reasonForRejection is fetched
//                         }
//                     }
//                 `;
//                 const response = await queryGraphql(query, { getAiAgentId: id });

//                 if (response.errors && response.errors.length > 0) {
//                     setError(response.errors[0].message || "Error fetching data.");
//                     setAiAgent(null);
//                 } else if (response.data && response.data.getAIAgent) {
//                     setAiAgent(response.data.getAIAgent);
//                 } else if (response.getAIAgent) { 
//                     setAiAgent(response.getAIAgent);
//                 } else {
//                     setError("AI Agent not found or unexpected response structure.");
//                     setAiAgent(null);
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching AI Agent data:", err);
//                 setError(err.message || "Failed to fetch AI Agent details.");
//                 setAiAgent(null);
//             } finally {
//                 setIsLoading(false);
//             }
//         };

//         fetchAIAgentData();
//     }, [id]); 

//     if (isLoading) {
//         return (
//             <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//                 <CircularProgress />
//                 <Typography sx={{ ml: 2 }}>Loading agent details...</Typography>
//             </Box>
//         );
//     }

//     if (error) {
//         return <Typography color="error" sx={{p:2}}>{error}</Typography>;
//     }

//     if (!aiAgent) {
//         return <Typography sx={{p:2}}>No agent data available. Ensure the agent ID is correct and the API is reachable.</Typography>;
//     }

//     const addedOnDate = aiAgent.createdAt ? moment(new Date(Number(aiAgent.createdAt))).format("DD/MM/YYYY") : 'N/A';
//     const resourceOwnerName = aiAgent.aIAgentOwner?.name || 'N/A';

//     const getStatusBackgroundColor = (status: string | undefined): string => {
//         if (!status) return "#F8F8F8"; // Default
//         const lowerStatus = status.toLowerCase();
//         if (lowerStatus === "active" || lowerStatus === "approved") {
//             return "#12B76A10"; // Light green tint
//         } else if (lowerStatus === "rejected") {
//             return "#F0443810"; // Light red tint
//         } else if (lowerStatus === "pending") {
//             return "#FDB02210"; // Light yellow/orange tint
//         }
//         return "#F8F8F8"; // Default for other statuses
//     };

//     const isRepoUrlVisible = aiAgent.repoUrl && 
//                              (aiAgent.status?.toLowerCase() === 'active' || aiAgent.status?.toLowerCase() === 'approved');

//     return (
//         <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
//             <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
//                 <Link underline="hover" color="inherit" href="/ai-agents" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/aisolutions/aiagent'); }}> AI Agents </Link>
//                 <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Agent Details</Typography>
//             </Breadcrumbs>

//             <Box display="flex" alignItems="center" mb={3}>
//                 <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/aisolutions/aiagent')}>
//                     <ArrowBackIosNewIcon fontSize="small" />
//                 </IconButton>
//                 <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Agent Details </Typography>
//             </Box>

//             <Box sx={{mx:0, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
//                 <Box sx={{
//                     p: 2.5,
//                     bgcolor: getStatusBackgroundColor(aiAgent.status), // Dynamic background color
//                     mb: 0, // Adjusted: No margin-bottom here, spacing handled by padding of next grid or explicit Box.
//                     mx: 0, 
//                     borderTopLeftRadius: "8px", 
//                     borderTopRightRadius: "8px",
//                 }}>
//                     <Box>
//                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
//                         <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
//                             {aiAgent.agentName}
//                         </Typography>
//                         {aiAgent.status === "pending" &&
//                             user?.roles?.some((role: string) => ["application_owner"].includes(role)) && ( // Using your condition
//                                 <Tooltip title="Edit AI Agent">
//                                     <IconButton
//                                         aria-label="edit ai agent"
//                                         onClick={() => navigate(`/aisolutions/aiagent/edit/${aiAgent.id}`)}
//                                         sx={{ 
//                                             // Using a simpler style for the IconButton, or you can use your previous border style
//                                             // border: `1px solid ${theme.palette.divider}`, borderRadius: '4px', padding: '4px' 
//                                         }}
//                                     >
//                                         <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit Agent"/>
//                                     </IconButton>
//                                 </Tooltip>
//                             )
//                         }
//                     </Box>
//                         <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
//                             Resource Owner: {resourceOwnerName} | Added On: {addedOnDate}
//                         </Typography>
//                     </Box>
//                 </Box>

//                 <Grid container spacing={3} sx={{p:3, pt: 2.5 /* Adjusted padding top */}}>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Project ID</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.projectId}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Region</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.region}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Service Name</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.serviceName}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={8} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Image URL</Typography>
//                         <Typography variant="body1"  sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333', wordBreak: 'break-all'}}>{aiAgent.imageUrl}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>GPU</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.gpu}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Environment</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.environment}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Application Name</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.applicationName}</Typography>
//                     </Grid>
                   
//                     {isRepoUrlVisible && (
//                         <Grid item xs={12} sm={5} md={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Repo URL</Typography>
//                             <Tooltip
//                                 sx={{ whiteSpace: "pre-line" }}
//                                 title={
//                                     <>
//                                         <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" fontWeight={700} color="common.white"> {/* Ensure white for tooltip */}
//                                             Repo URL
//                                         </Typography>
//                                         <br />
//                                         <Link
//                                             href={aiAgent.repoUrl} // repoUrl is checked by isRepoUrlVisible
//                                             target="_blank"
//                                             rel="noopener noreferrer"
//                                             sx={{
//                                                 display: 'block',
//                                                 wordBreak: 'break-all',
//                                                 color: '#ffffff', 
//                                                 textDecoration: 'none',
//                                                 fontFamily: "Gilroy-Regular",
//                                                 fontSize: "14px",
//                                                 '&:hover': {
//                                                     textDecoration: 'underline',
//                                                 },
//                                             }}
//                                         >
//                                             {aiAgent.repoUrl}
//                                         </Link>
//                                         <br/>
//                                     </>
//                                 }
//                                 slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
//                             >
//                                 <Avatar
//                                     src="/assets/info.svg"
//                                     alt="info"
//                                     sx={{ height: 16, width: 16, /* marginLeft: "4px", // Potentially remove if label is above */ cursor: 'pointer' }}
//                                 />
//                             </Tooltip>
//                         </Grid>
//                     )}
//                 </Grid>

//                 <Box sx={{ mt: 0, p: 2.5, borderTop: "1px solid #e0e0e0" }}>
//                     <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                         Model Details
//                     </Typography>
//                     <Grid container spacing={3}>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Model Name</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.modelName}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Model Temperature</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.modelTemperature}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Model Tokens</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.modelMaxTokens}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Model Streaming</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{aiAgent.modelStreaming ? "True" : "False"}</Typography>
//                         </Grid>
//                     </Grid>
//                 </Box>
                
//                 {aiAgent.status && aiAgent.status.toLowerCase() === 'rejected' && aiAgent.reasonForRejection && (
//                     <Box sx={{ mt: 0, p: 2.5, borderTop: "1px solid #e0e0e0" }}>
//                         <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color: theme.palette.error.main, pb:"0.5vh" }}>
//                             Reason for Rejection
//                         </Typography>
//                         <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#333333'}}>
//                             {aiAgent.reasonForRejection}
//                         </Typography>
//                     </Box>
//                 )}
//             </Box>
//         </Container>
//     );
// };


// const AIAgentDetailsPage: React.FC = () => {
//     const theme = useTheme();
//     const getInitialDrawerState = () => {
//         if (typeof window !== 'undefined') {
//             const storedState = localStorage.getItem('isFaradayDrawerOpen');
//             return storedState !== null ? JSON.parse(storedState) : true;
//         }
//         return true;
//     };
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//         if (typeof window !== 'undefined') {
//             localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
//         }
//     };

//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer
//                 index={"aisolutions-aiagent"} 
//                 onStateChange={handleDrawerStateChange}
//             />
//             <MiniBot/> 
//             <Box
//                 component="main" 
//                 sx={{
//                     flexGrow: 1,
//                     p: 0, 
//                     transition: theme.transitions.create('margin', { 
//                         easing: theme.transitions.easing.sharp,
//                         duration: theme.transitions.duration.enteringScreen,
//                     }),
//                     marginLeft: `${currentMarginLeft}vh`, 
//                 }}
//             >
//                 <AIAgentDetailsPageContent /> 
//             </Box>
//         </Box>
//     );
// };

// export default AIAgentDetailsPage;